/**
 * Avatar — a crew headshot with a graceful initials fallback.
 *
 * Renders the photo when `src` is a usable URL; otherwise (or if the image
 * fails to load) shows the person's initials in a circle. Used on the public
 * status page, the crew task-detail roster, and the closeout recap.
 */
import { useState } from 'react';
import { cn } from '@/lib/cn';

export function initialsOf(name: string): string {
  const parts = name.split(' ').filter(Boolean);
  const initials = parts
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
  return initials || '?';
}

export function Avatar({
  name,
  src,
  className,
}: {
  name: string;
  src?: string | null;
  className?: string;
}) {
  const [errored, setErrored] = useState(false);
  const showImg = !!src && !errored;
  return (
    <span
      title={name}
      className={cn(
        'inline-flex items-center justify-center rounded-full bg-fill border border-line overflow-hidden text-[10px] font-bold text-ink-dim flex-shrink-0 w-8 h-8',
        className,
      )}
    >
      {showImg ? (
        <img
          src={src ?? undefined}
          alt={name}
          className="w-full h-full object-cover"
          onError={() => setErrored(true)}
        />
      ) : (
        initialsOf(name)
      )}
    </span>
  );
}
